import { reactive, ref } from 'vue'
import { DevicePid } from '@/define/define'
import { STORAGE_KEYS } from '@libs/define/storage_key'
import { mixHexColor, normalizeHexColor, withAlpha as toRgba } from '@/utils/color'
import { closeOtherAppearancePanels, notifyAppearancePanelClosed, notifyAppearancePanelOpened, registerAppearancePanelCloser } from '@/service/appearance_panel_registry'

const APPEARANCE_PANEL_ID = 'keyboard_appearance'

export type KeyboardColorPreset = 'warm' | 'pearl' | 'mint' | 'ocean' | 'aurora' | 'carbon' | 'custom'
export type KeyboardCustomColorKey = 'board' | 'key' | 'text' | 'accent'

type KeyboardCustomColors = Record<KeyboardCustomColorKey, string>

type KeyboardAppearanceState = {
  colorPreset: KeyboardColorPreset
  customColors: KeyboardCustomColors
}

type AppearanceCssTokens = Record<string, string>

type KeyboardAppearanceStorage = KeyboardAppearanceState & {
  version: 1
}

const KEYBOARD_APPEARANCE_SCHEMA_VERSION = 1 as const

const DEFAULT_APPEARANCE: KeyboardAppearanceState = {
  colorPreset: 'warm',
  customColors: {
    board: '#fff6f0',
    key: '#ffffff',
    text: '#2f241d',
    accent: '#ff8f60',
  },
}

/**
 * 键盘外观预设的单一事实来源。
 *
 * 每条预设包含：
 * - `value` / `labelKey`：UI 与 state 共用的标识。
 * - `colors`：写入 CSS 变量的真实色板（apply 链路）。
 * - `swatches`：卡片上三个小色块（通常 = [board, key, accent]，由 colors 自动派生，
 *   仅当历史上某个预设需要差异化展示时再手动覆盖）。
 *
 * 如需新增预设：在数组中追加一条；如需修改某个预设的颜色：只改 `colors`，
 * 卡片预览会自动跟随，不会再出现"卡片显示一种颜色，按下后变成另一种颜色"的双源问题。
 */
const keyboardColorPresets = [
  { value: 'warm' as const, labelKey: 'keyboard_color_warm', colors: { board: '#fff6f0', key: '#ffffff', text: '#2f241d', accent: '#ff8f60' } },
  { value: 'pearl' as const, labelKey: 'keyboard_color_pearl', colors: { board: '#edf5ff', key: '#ffffff', text: '#233244', accent: '#7fd8ff' } },
  { value: 'mint' as const, labelKey: 'keyboard_color_mint', colors: { board: '#1b1211', key: '#44322f', text: '#fff0ea', accent: '#ff7a52' } },
  { value: 'ocean' as const, labelKey: 'keyboard_color_ocean', colors: { board: '#09131d', key: '#2b4b66', text: '#eef8ff', accent: '#39d5ff' } },
  { value: 'aurora' as const, labelKey: 'keyboard_color_aurora', colors: { board: '#161c30', key: '#f5f7ff', text: '#25304a', accent: '#8c7dff' } },
  { value: 'carbon' as const, labelKey: 'keyboard_color_carbon', colors: { board: '#171a20', key: '#353b46', text: '#f6f8fb', accent: '#5ce0ad' } },
] as const

type NamedColorPreset = Exclude<KeyboardColorPreset, 'custom'>

const colorPresetColors: Record<NamedColorPreset, KeyboardCustomColors> = keyboardColorPresets.reduce(
  (acc, preset) => {
    acc[preset.value] = { ...preset.colors }
    return acc
  },
  {} as Record<NamedColorPreset, KeyboardCustomColors>,
)

export type AppearancePresetColors = {
  boardBg: string
  keyBg: string
  accent: string
}

export type KeyboardColorPresetOption = {
  value: NamedColorPreset
  labelKey: string
  colors: AppearancePresetColors
}

export const keyboardColorPresetOptions: KeyboardColorPresetOption[] = keyboardColorPresets.map((preset) => ({
  value: preset.value,
  labelKey: preset.labelKey,
  colors: {
    boardBg: preset.colors.board,
    keyBg: preset.colors.key,
    accent: preset.colors.accent,
  },
}))

/**
 * 板形 / 键帽形态相关 token —— 历史上有 soft/outline/glass 三种 stylePreset，
 * UI 已下线，仅保留 soft 一组常量值，避免散落的 vk 组件读 var 时拿到 undefined。
 */
const staticBoardTokens: AppearanceCssTokens = {
  '--vk-board-shadow': '0 8px 24px rgba(0, 0, 0, 0.05)',
  '--vk-key-radius': 'var(--radius-s)',
  '--vk-key-shadow': '0 2px 6px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
  '--vk-key-hover-shadow': '0 6px 12px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.04)',
}

export const keyboardCustomColorFields = [
  { key: 'board' as const, labelKey: 'keyboard_color_board' },
  { key: 'key' as const, labelKey: 'keyboard_color_key' },
  { key: 'text' as const, labelKey: 'keyboard_color_text' },
  { key: 'accent' as const, labelKey: 'keyboard_color_accent' },
]

const appearanceState = reactive<KeyboardAppearanceState>({
  ...DEFAULT_APPEARANCE,
  customColors: { ...DEFAULT_APPEARANCE.customColors },
})
export const keyboardAppearancePanelVisible = ref(false)
export const keyboardAppearancePreviewPid = ref<DevicePid>(DevicePid.None)

let initialized = false

const presetColorKeys: readonly KeyboardColorPreset[] = [...keyboardColorPresets.map((p) => p.value), 'custom']

/**
 * 历史 colorPreset 重命名映射。仅用于读取旧 localStorage 数据时的兼容兜底。
 * @deprecated 数据已稳定一段时间，可在下个发版周期后清理。
 */
const legacyColorPresetAliases: Record<string, NamedColorPreset> = {
  glacier: 'pearl',
  lava: 'mint',
  esports: 'ocean',
  nebula: 'aurora',
  stealth: 'carbon',
}

const normalizeColorPreset = (value: unknown): KeyboardColorPreset => {
  if (typeof value !== 'string') {
    return DEFAULT_APPEARANCE.colorPreset
  }

  if ((presetColorKeys as readonly string[]).includes(value)) {
    return value as KeyboardColorPreset
  }

  return legacyColorPresetAliases[value] ?? DEFAULT_APPEARANCE.colorPreset
}

const canUseStorage = (): boolean => {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
}

const normalizeCustomColors = (value?: Partial<KeyboardCustomColors> | null): KeyboardCustomColors => {
  const fallback = DEFAULT_APPEARANCE.customColors
  return {
    board: normalizeHexColor(value?.board, fallback.board),
    key: normalizeHexColor(value?.key, fallback.key),
    text: normalizeHexColor(value?.text, fallback.text),
    accent: normalizeHexColor(value?.accent, fallback.accent),
  }
}

const normalizeAppearance = (value?: Partial<KeyboardAppearanceState> | null): KeyboardAppearanceState => {
  return {
    colorPreset: normalizeColorPreset(value?.colorPreset),
    // 始终加载用户存储的自定义颜色，不用预设颜色覆盖，与软件主题架构对齐
    customColors: normalizeCustomColors(value?.customColors),
  }
}

const createColorTokens = (colors: KeyboardCustomColors): AppearanceCssTokens => {
  const accentHighlight = mixHexColor(colors.accent, '#ffffff', 0.14)
  return {
    '--vk-board-bg': toRgba(colors.board, 0.88),
    '--vk-board-border': `1px solid ${toRgba(colors.board, 0.76)}`,
    '--vk-key-bg': colors.key,
    '--vk-key-hover-bg': mixHexColor(colors.key, '#ffffff', 0.12),
    '--vk-key-text': colors.text,
    '--vk-texture-color': colors.text,
    '--vk-accent-color': colors.accent,
    '--vk-accent-gradient': `linear-gradient(180deg, ${accentHighlight} 0%, ${colors.accent} 100%)`,
    '--vk-accent-soft-bg': toRgba(colors.accent, 0.14),
    '--vk-accent-shadow': `0 8px 24px ${toRgba(colors.accent, 0.35)}`,
    '--vk-accent-shadow-soft': `0 0 12px ${toRgba(colors.accent, 0.4)}`,
  }
}

const getMergedTokens = (): AppearanceCssTokens => {
  // 预设模式读预设颜色，自定义模式读用户设置的颜色——两套颜色互不干扰
  const activeColors = appearanceState.colorPreset === 'custom' ? appearanceState.customColors : colorPresetColors[appearanceState.colorPreset]
  return {
    ...createColorTokens(activeColors),
    ...staticBoardTokens,
  }
}

const applyAppearance = () => {
  if (typeof document === 'undefined') {
    return
  }

  const root = document.documentElement
  const tokens = getMergedTokens()

  Object.entries(tokens).forEach(([token, value]) => {
    root.style.setProperty(token, value)
  })
}

const persistAppearance = () => {
  if (!canUseStorage()) {
    return
  }

  const payload: KeyboardAppearanceStorage = {
    version: KEYBOARD_APPEARANCE_SCHEMA_VERSION,
    colorPreset: appearanceState.colorPreset,
    customColors: appearanceState.customColors,
  }

  localStorage.setItem(STORAGE_KEYS.VIRTUAL_KEYBOARD_APPEARANCE, JSON.stringify(payload))
}

const syncAppearance = () => {
  applyAppearance()
  persistAppearance()
}

export const initializeVirtualKeyboardAppearance = () => {
  if (initialized) {
    return
  }

  initialized = true

  if (canUseStorage()) {
    try {
      const storedValue = localStorage.getItem(STORAGE_KEYS.VIRTUAL_KEYBOARD_APPEARANCE)
      if (storedValue) {
        // 兼容读取：v1 带 version 字段；旧数据无 version，按当前结构解析后由 normalizeAppearance 兜底。
        const parsed = JSON.parse(storedValue) as Partial<KeyboardAppearanceStorage>
        Object.assign(appearanceState, normalizeAppearance(parsed))
      }
    } catch (_error) {
      Object.assign(appearanceState, { ...DEFAULT_APPEARANCE })
    }
  }

  applyAppearance()
  registerAppearancePanelCloser(APPEARANCE_PANEL_ID, closeKeyboardAppearancePanel)
}

export const setKeyboardColorPreset = (preset: KeyboardColorPreset) => {
  if (appearanceState.colorPreset === preset) {
    return
  }

  // 只切换 preset 标识，不修改 customColors——用户的自定义颜色始终保留
  appearanceState.colorPreset = preset
  syncAppearance()
}

export const setKeyboardCustomColor = (key: KeyboardCustomColorKey, value: string) => {
  const normalizedValue = normalizeHexColor(value, appearanceState.customColors[key])

  if (appearanceState.customColors[key] === normalizedValue && appearanceState.colorPreset === 'custom') {
    return
  }

  appearanceState.customColors[key] = normalizedValue
  appearanceState.colorPreset = 'custom'
  syncAppearance()
}

export const resetVirtualKeyboardAppearance = () => {
  Object.assign(appearanceState, {
    ...DEFAULT_APPEARANCE,
    customColors: { ...DEFAULT_APPEARANCE.customColors },
  })
  syncAppearance()
}

export const openKeyboardAppearancePanel = () => {
  closeOtherAppearancePanels(APPEARANCE_PANEL_ID)
  keyboardAppearancePanelVisible.value = true
  notifyAppearancePanelOpened(APPEARANCE_PANEL_ID)
}

export const closeKeyboardAppearancePanel = () => {
  keyboardAppearancePanelVisible.value = false
  keyboardAppearancePreviewPid.value = DevicePid.None
  notifyAppearancePanelClosed(APPEARANCE_PANEL_ID)
}

export const setKeyboardAppearancePreviewPid = (pid: DevicePid) => {
  keyboardAppearancePreviewPid.value = pid
}

export const clearKeyboardAppearancePreviewPid = () => {
  keyboardAppearancePreviewPid.value = DevicePid.None
}

export const virtualKeyboardAppearance = appearanceState
